import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Loader2, RefreshCw, XCircle } from "lucide-react";
import { post } from "@/lib/api";
import { cn } from "@/lib/utils";

const ETATS = {
  synchronise: { icon: CheckCircle2, txt: "Synchronisé", cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
  ecart: { icon: AlertTriangle, txt: "Écart détecté", cls: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  erreur: { icon: XCircle, txt: "Erreur de synchronisation", cls: "text-red-400 border-red-500/40 bg-red-500/10" },
};

/** Indicateur de réconciliation production → stock + déclenchement manuel (filet de sécurité). */
export const SynchroStatut = ({ statut, peutAuditer }) => {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => post("/inventaire/reconciliation"),
    onSuccess: (r) => {
      toast.success(`${r.evenements_verifies} événements vérifiés — ${r.mouvements_crees} mouvement(s) créé(s) — ${r.deja_synchronises} déjà synchronisés — ${r.erreurs} erreur(s)`);
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(e.message),
  });
  const etat = m.isError ? ETATS.erreur : ETATS[statut?.statut] || ETATS.synchronise;
  const Icon = m.isPending ? Loader2 : etat.icon;
  const d = statut?.dernier_resultat;
  return (
    <div data-testid="synchro-statut" className="panel px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <span data-testid="synchro-badge"
        className={cn("h-6 px-2 rounded-sm border text-[11px] font-semibold flex items-center gap-1.5", etat.cls)}>
        <Icon className={cn("h-3.5 w-3.5", m.isPending && "animate-spin")} />
        {m.isPending ? "Synchronisation en cours…" : etat.txt}
      </span>
      <span className="text-[11px] text-muted-foreground font-mono">
        {statut?.mouvements_production ?? 0}/{statut?.evenements_attendus ?? 0} événements de production en stock
        {statut?.ecart ? ` · ${statut.ecart} manquant(s)` : ""}
      </span>
      <span data-testid="synchro-derniere" className="text-[11px] text-muted-foreground font-mono">
        Dernière synchro : {statut?.derniere_synchro || "—"}
        {d ? ` (${d.evenements_verifies} vérifiés · ${d.mouvements_crees} créés · ${d.deja_synchronises} déjà · ${d.erreurs} erreur(s))` : ""}
      </span>
      {peutAuditer && (
        <button data-testid="btn-synchroniser" disabled={m.isPending} onClick={() => m.mutate()}
          className="h-7 px-3 ml-auto bg-card border border-border rounded-sm text-[11px] font-semibold flex items-center gap-1.5 hover:bg-muted disabled:opacity-50">
          <RefreshCw className={cn("h-3.5 w-3.5 text-primary", m.isPending && "animate-spin")} />
          Synchroniser la production
        </button>
      )}
    </div>
  );
};
